import React from 'react';
import TextField from '@mui/material/TextField';
import { OptionValue } from './EnhancedSelect';
import {
  Box,
  Typography,
  Autocomplete,
  autocompleteClasses,
  Chip,
  CircularProgress,
  Popper,
  SxProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { AutocompleteRenderValueGetItemProps } from '@mui/material/Autocomplete';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import { styled } from '@mui/material/styles';
import { t } from 'i18next';

/** MUI only types `key` on getItemProps when `multiple` is literally true.
 * Our `multiple` prop is a boolean, so we narrow to the multiple-mode shape. */
type MultipleItemProps = ReturnType<AutocompleteRenderValueGetItemProps<true>>;

type SelectContextValue = {
  disabled?: boolean;
  tipText?: string;
  tipDisabled?: boolean;
};

export type ChangeReason =
  | 'createOption'
  | 'selectOption'
  | 'removeOption'
  | 'blur'
  | 'clear';

type Props = {
  label: string;
  data: Array<OptionValue>;
  loading?: boolean;
  value: any;
  isOptionEqualToValue?: (option: OptionValue, value: OptionValue) => boolean;
  multiple?: boolean;
  onChange: (value: any, reason: ChangeReason) => void;
  id?: string;
  placeholder?: string;
  containerClass?: string;
  disabled?: boolean;
  shouldVirtualize?: boolean;
  tipText?: string;
  tipDisabled?: boolean;
  /** Keep the list open after each pick. Defaults to false so existing callers stay unchanged. */
  disableCloseOnSelect?: boolean;
  sx?: SxProps;
  /** When provided, options are grouped by the returned string (e.g. inventory center code). */
  groupBy?: (option: OptionValue) => string;
};

const SelectContext = React.createContext<SelectContextValue>({});

/**
 * EnhancedMultiSelect is a component that wraps the Material-UI Autocomplete component
 * to provide a customized multi-select input with optional virtualization for handling
 * large datasets efficiently.
 *
 * The component is context-aware and supports additional features like displaying tooltips
 * and dynamically enabling virtualization based on the dataset size for improved performance.
 *
 * @param label - The label for the autocomplete input field.
 * @param data - The options to be displayed in the dropdown list. Each option should have a label and a value.
 * @param loading - If true, indicates that the options are currently being loaded. This can be used to display a loading indicator in the UI. Optional.
 * @param value - The currently selected value(s) in the autocomplete.
 * @param [function] isOptionEqualToValue - A custom equality function to compare options with values.
 * @param  multiple - If true, allows multiple values to be selected. Defaults to true.
 * @param onChange - Callback function that is called when the selected value changes. It receives the new value as an argument.
 * @param [id] - The unique identifier for the input field, used for accessibility purposes. Optional.
 * @param [placeholder] - Placeholder text for the input field when it is empty. Optional.
 * @param [containerClass] - Additional CSS class(es) for styling the container of the input field. Optional.
 * @param [disabled] - If true, disables the input field. Optional.
 * @param [shouldVirtualize] - If true, uses a virtualized list for displaying options. This improves performance with large datasets. Optional.
 * @param [tipText] - Text for a tooltip that can be displayed alongside the input field. Optional.
 * @param [tipDisabled] - If true, disables the display of the tooltip. Optional.
 * @param [sx] - Custom styles that can be applied to the Autocomplete component using Material-UIs' styling solution. Optional.
 */
const EnhancedMultiSelect = ({
  label,
  id,
  data,
  loading,
  isOptionEqualToValue,
  multiple = true,
  value = multiple ? [] : null,
  onChange,
  placeholder,
  containerClass,
  disabled,
  shouldVirtualize = false,
  tipText,
  tipDisabled,
  disableCloseOnSelect = false,
  sx,
  groupBy,
}: Props) => {
  /**
   * Handles the change event when the selected value(s) change in the Autocomplete component.
   * MUI Autocomplete types the value as OptionValue | OptionValue[] | null depending on the
   * multiple prop, so we accept the union here and pass it straight through to the caller.
   * @param _
   * @param newValue
   * @param reason
   */
  const handleChange = (
    _: React.SyntheticEvent,
    newValue: OptionValue | OptionValue[] | null,
    reason: ChangeReason,
  ) => {
    onChange(newValue, reason);
  };

  /**
   * Default equality function to compare options based on their values.
   * @param option
   * @param value
   */
  const defaultEqualityFunction = (option: OptionValue, value: OptionValue) =>
    option.value === value.value;

  return (
    <SelectContext.Provider value={{ disabled, tipText, tipDisabled }}>
      <Autocomplete
        loading={loading}
        loadingText={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={20} />
            <Typography variant='body2' color='textSecondary'>
              {t('loading')}
            </Typography>
          </Box>
        }
        sx={sx}
        multiple={multiple}
        disableCloseOnSelect={disableCloseOnSelect}
        id={id}
        disabled={disabled}
        options={data}
        value={value}
        onChange={handleChange}
        groupBy={groupBy}
        isOptionEqualToValue={(option, value) =>
          isOptionEqualToValue
            ? isOptionEqualToValue(option, value)
            : defaultEqualityFunction(option, value)
        }
        renderValue={
          multiple
            ? (value, getItemProps) =>
                (value as OptionValue[]).map((option, index) => {
                  const { key, ...itemProps } = getItemProps({
                    index,
                  }) as MultipleItemProps;
                  return (
                    <Chip
                      key={key}
                      {...itemProps}
                      label={option.label}
                      size='small'
                    />
                  );
                })
            : undefined
        }
        renderInput={(params) => (
          <TextField
            className={containerClass}
            {...params}
            label={label}
            placeholder={placeholder}
            variant={'standard'}
            slotProps={{
              inputLabel: {
                htmlFor: id,
                shrink: true,
              },
            }}
          />
        )}
        slots={{
          popper: StyledPopper as any,
        }}
        slotProps={{
          listbox: {
            component: shouldVirtualize ? ListboxComponent : undefined,
          },
        }}
      />
    </SelectContext.Provider>
  );
};

/**
 * Optimizes the rendering of EnhancedMultiSelect by memoizing it. This prevents unnecessary re-renders
 * unless the `value` prop changes or the length of the `data` prop changes. It's important to note that
 * if closure issues arise — where the component does not re-render in response to updated state or props
 * not included in this comparison — the props comparison function here may need to be updated to
 * include additional props that affect rendering (i.e., such as `onChange`).
 */
export default React.memo(
  EnhancedMultiSelect,
  (p, n) =>
    p.value === n.value &&
    p.data.length === n.data.length &&
    p.loading === n.loading,
);

// Listbox padding constant to ensure consistent spacing within the listbox component.
const LISTBOX_PADDING = 8;

/**
 * Renders an individual row within the virtualized list. This function is called by react-window.
 * It clones the original option element to apply additional inline styling for proper alignment.
 *
 * @param {ListChildComponentProps} props - The props passed by react-window for rendering a row.
 * @returns {React.ReactElement} A cloned element of the option with adjusted styling.
 */
const renderRow = (props: ListChildComponentProps): React.ReactElement => {
  const { data, index, style } = props;
  const option = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  return React.cloneElement(option, {
    style: inlineStyle,
  });
};

// Context to pass down additional props to the outer element of the VariableSizeList.
const OuterElementContext = React.createContext({});

/**
 * A forwardRef component that renders a div and applies any passed props.
 * This component is intended to be used as the outer container for the VariableSizeList.
 *
 * @param {React.HTMLAttributes<HTMLElement>} props - Props to be spread onto the div.
 * @param {React.Ref<HTMLDivElement>} ref - Ref forwarded to the div.
 * @returns {React.ReactElement} A div with applied props and ref.
 */
const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

/**
 * Custom hook to reset the cache of the VariableSizeList when the data changes.
 * This ensures that the list correctly recalculates item sizes after data updates.
 *
 * @param {any} data - Dependency to trigger cache reset. Typically, the dataset or its length.
 * @returns {React.Ref<VariableSizeList>} A ref attached to the VariableSizeList instance.
 */
const useResetCache = (data: any): React.Ref<VariableSizeList> => {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
};

/**
 * Custom ListboxComponent for MUI Autocomplete, implemented with react-window for virtualization.
 * It renders a virtualized list of options, improving performance for large datasets.
 *
 * @param {React.HTMLAttributes<HTMLElement>} props - Standard HTML attributes for the component.
 * @param {React.Ref<HTMLDivElement>} ref - Ref forwarding for the component.
 * @returns {React.ReactElement} The virtualized list component integrated into MUI Autocomplete.
 */
const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData: React.ReactElement[] = [];
  (children as React.ReactElement[]).forEach(
    (item: React.ReactElement & { children?: React.ReactElement[] }) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    },
  );

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  // Determine the dynamic height of the list based on the number of items.
  const getHeight = () =>
    (itemCount > 8 ? 8 * itemSize : itemCount * itemSize) + 2 * LISTBOX_PADDING;

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width='100%'
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType='ul'
          itemSize={() => itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

/**
 * Custom Popper component for MUI Autocomplete, implemented with styled-components for custom styling.
 *
 * Applies custom styles to the inner list (`ul`) within the listbox to remove default padding and margin.
 * This adjustment is crucial for:
 *
 * 1. Preventing an unwanted horizontal scrollbar caused by the default padding or margin settings.
 *    By setting both padding and margin to 0, we ensure the dropdown content fits within the designated
 *    area without overflowing, which could otherwise introduce a scrollbar.
 *
 * 2. Ensuring consistent box sizing across browsers and contexts. The 'box-sizing: border-box' style
 *    is applied to make sure the padding and border of the listbox are included in the total width and
 *    height. This is important for maintaining layout consistency and avoiding unexpected overflow
 *    that could lead to scrollbars or misalignment.
 *
 * 3. Styling group headers (rendered when the `groupBy` prop is used). The headers
 *    stay neutral and compact so they separate groups without competing with options.
 */
const StyledPopper = styled(Popper)(({ theme }) => ({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
  [`& .${autocompleteClasses.groupLabel}`]: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderTop: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.6,
    minHeight: 30,
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    textTransform: 'uppercase',
    top: 0,
    // Keep long center names on a single line so the header row stays the same height
    // as option rows; truncate with an ellipsis if they'd overflow the listbox width.
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  [`& .${autocompleteClasses.groupUl} .${autocompleteClasses.option}`]: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  [`& .${autocompleteClasses.groupUl} .${autocompleteClasses.option}:first-of-type`]:
    {
      marginTop: theme.spacing(0.5),
    },
  [`& .${autocompleteClasses.listbox} > li:first-of-type .${autocompleteClasses.groupLabel}`]:
    {
      borderTop: 0,
      marginTop: 0,
    },
}));
